/**
 * Rogue Alloy home/tyres motion (content review package P2, design spec
 * section 9 and section 10/P2 item 2).
 *
 * Framework-free ES module, loaded with `defer` from sections/rogue-stats.liquid.
 * Owns the count-up behaviour for the "Numbers band": each stat marked
 * `data-rogue-stat-count-up="true"` counts from 0 up to its final value once,
 * over 900ms, the first time 40% of its band is visible, and only when
 * `prefers-reduced-motion` allows it. The final value is always server-
 * rendered (sections/rogue-stats.liquid), so a reader with no JS, or with
 * reduced motion, sees the real number immediately and this script never
 * runs (or runs once and repaints the same number it started with).
 *
 * The marquee (rogue-brand-strip) and the gallery hover scale need no JS:
 * both are pure CSS, gated by the same `prefers-reduced-motion` media query,
 * in assets/rogue-home.css.
 *
 * Unit tested by tests/rogue-motion.test.js.
 */

/**
 * Parses a stat's `data-rogue-stat-target` attribute into a non-negative
 * whole number, or null if it isn't one (spec: "numbers only, never words" -
 * a stat with a non-numeric `value` setting is left alone rather than
 * animated to 0 or NaN).
 * @param {string|null} raw
 * @returns {number|null}
 */
export function parseStatTarget(raw) {
  if (typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  return Number.parseInt(trimmed, 10);
}

/**
 * Formats a whole number for display mid-count or at rest: with a thousands
 * separator only when the stat's own final value was authored with one (the
 * `data-rogue-stat-grouped` flag, set server-side from whether the block's
 * `value` setting contains a comma) - so a year like "2020" never grows a
 * comma while a count like "23,000" keeps one throughout.
 * @param {number} value
 * @param {boolean} grouped
 * @returns {string}
 */
export function formatStatNumber(value, grouped) {
  const whole = Math.round(value);
  return grouped ? whole.toLocaleString('en-GB') : String(whole);
}

/**
 * Eases a 0-1 progress value for the count-up (a gentle deceleration,
 * matching the shared --rogue-ease cubic-bezier's general shape closely
 * enough for a 900ms count that only needs to look unhurried; CSS custom
 * properties are not readable as JS easing functions, so this is a plain
 * approximation rather than a parse of --rogue-ease itself).
 * @param {number} t 0 to 1
 * @returns {number} 0 to 1
 */
export function easeOutStat(t) {
  const clamped = Math.min(1, Math.max(0, t));
  return 1 - (1 - clamped) ** 3;
}

const STAT_DURATION_MS = 900;

/**
 * Animates a single stat element from 0 to its target over
 * STAT_DURATION_MS, updating only the aria-hidden number span (a sibling
 * visually-hidden span keeps the real final value announced throughout, so
 * assistive tech is never told "0").
 * @param {Element} item
 */
function animateStat(item) {
  const target = parseStatTarget(item.getAttribute('data-rogue-stat-target'));
  const numberEl = item.querySelector('[data-rogue-stat-number]');
  if (target === null || target <= 0 || !numberEl) return;

  const grouped = item.getAttribute('data-rogue-stat-grouped') === 'true';
  const suffix = item.getAttribute('data-rogue-stat-suffix') || '';
  const start = typeof performance !== 'undefined' ? performance.now() : Date.now();

  const tick = (now) => {
    const elapsed = now - start;
    const progress = Math.min(1, elapsed / STAT_DURATION_MS);
    const current = Math.round(target * easeOutStat(progress));
    numberEl.textContent = formatStatNumber(current, grouped) + suffix;

    if (progress < 1) {
      requestAnimationFrame(tick);
    } else {
      numberEl.textContent = formatStatNumber(target, grouped) + suffix;
    }
  };

  numberEl.textContent = formatStatNumber(0, grouped) + suffix;
  requestAnimationFrame(tick);
}

/**
 * Wires up every `[data-rogue-stats]` band on the page: watches it with an
 * IntersectionObserver (40% visible) and, the first time that fires, starts
 * every eligible stat inside counting up. Does nothing when reduced motion
 * is requested, when there is no IntersectionObserver, or when a band has
 * no count-up stats - reduced motion and no-JS readers keep the
 * server-rendered final values (spec 9).
 * @param {Document|Element} [root]
 */
export function initRogueStats(root = document) {
  if (typeof window === 'undefined' || typeof root.querySelectorAll !== 'function') return;

  const prefersReducedMotion =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (prefersReducedMotion || typeof IntersectionObserver === 'undefined') return;

  const bands = root.querySelectorAll('[data-rogue-stats]');
  bands.forEach((band) => {
    const items = Array.from(
      band.querySelectorAll('[data-rogue-stat][data-rogue-stat-count-up="true"]')
    );
    if (items.length === 0) return;

    let animated = false;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !animated) {
            animated = true;
            items.forEach(animateStat);
            observer.disconnect();
          }
        });
      },
      { threshold: 0.4 }
    );
    observer.observe(band);
  });
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initRogueStats());
  } else {
    initRogueStats();
  }
}
